import { useState } from "react";
import { AlertTriangle, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useAttributionReassignmentFlag } from "@/hooks/queries/useAttributionReassignmentFlag";
import { settingsApi } from "@/lib/settings.api";
import { getApiErrorMessage } from "../settingsPage.helpers";

// Super_admin-only toggle for the attribution_reassignment_enabled flag
// (2026-10-02) — controls whether manager/admin can use any "Réattribuer"
// action anywhere in the app. super_admin itself is never blocked by this
// flag; this panel exists purely to let them open/close it for others.
export function AttributionReassignmentPanel() {
  const { data: enabled, isLoading } = useAttributionReassignmentFlag();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      await settingsApi.update("attribution_reassignment_enabled", enabled ? "false" : "true");
      await queryClient.invalidateQueries({
        queryKey: ["settings", "attribution-reassignment-status"],
      });
      toast.success(enabled ? "Correction d'attribution désactivée." : "Correction d'attribution activée.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de modifier ce réglage."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <UserCog size={15} className="text-brand-gold-dark" />
        <div>
          <h3 className="text-sm font-semibold text-foreground">Activer la correction d'attribution</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Permet temporairement de réattribuer des documents et mouvements existants à un autre
            employé. À désactiver après correction des données.
          </p>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border border-border px-3 py-2.5">
          <span className="text-xs text-body">
            Statut actuel :{" "}
            <span className={enabled ? "font-semibold text-warning-text" : "font-semibold text-muted-foreground"}>
              {isLoading ? "…" : enabled ? "Activée" : "Désactivée"}
            </span>
          </span>
          <Button
            size="sm"
            variant={enabled ? "destructive" : "default"}
            onClick={toggle}
            disabled={isLoading || busy}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : null}
            {enabled ? "Désactiver" : "Activer"}
          </Button>
        </div>

        <div className="flex items-start gap-1.5 text-[10.5px] text-warning-text bg-warning-bg border border-warning-border rounded px-2.5 py-1.5">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>À utiliser uniquement pendant une phase de correction des données.</span>
        </div>
      </div>
    </section>
  );
}
